import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Brain, ChevronDown } from 'lucide-react'
import { UserDropdown } from './UserDropdown'

export const Header: React.FC = () => {
  const location = useLocation()
  const [showAnalysisDropdown, setShowAnalysisDropdown] = useState(false)
  const [showWheelsDropdown, setShowWheelsDropdown] = useState(false)

  const analysisTypes = [
    {
      path: '/decision-analysis/pros-cons',
      name: 'Pros & Cons Analysis',
      description: 'Analyze pros, cons, risks and opportunities',
      icon: '⚖️'
    },
    {
      path: '/decision-analysis/causal-chain',
      name: 'Causal Chain Analysis',
      description: 'Show decision outcomes in causal chains',
      icon: '🔗'
    },
    {
      path: '/decision-analysis/context',
      name: 'Context Analysis',
      description: 'Personalized analysis with profiles',
      icon: '👤'
    },
    {
      path: '/decision-analysis/multi-option',
      name: 'Multi-option Analysis',
      description: 'Compare multiple options systematically',
      icon: '📊'
    }
  ]

  const wheelTools = [
    {
      path: '/tools/name-picker',
      name: 'Name Picker Wheel',
      description: 'Randomly pick a name from a list of names',
      icon: '👤'
    },
    {
      path: '/tools/team-picker',
      name: 'Team Picker Wheel',
      description: 'Randomly group names into teams',
      icon: '👥'
    },
    {
      path: '/tools/yesno-picker',
      name: 'Yes No Picker Wheel',
      description: 'Randomly pick Yes or No',
      icon: '✅'
    },
    {
      path: '/tools/number-picker',
      name: 'Number Picker Wheel',
      description: 'Randomly pick a number from a range or list',
      icon: '🔢'
    },
    {
      path: '/tools/letter-picker',
      name: 'Letter Picker Wheel',
      description: 'Randomly pick a letter from A-Z',
      icon: '🔤'
    },
    {
      path: '/tools/country-picker',
      name: 'Country Picker Wheel',
      description: 'Randomly pick a country',
      icon: '🌍'
    },
    {
      path: '/tools/state-picker',
      name: 'State Picker Wheel',
      description: 'Randomly pick a US state',
      icon: '🗽'
    },
    {
      path: '/tools/color-picker',
      name: 'Color Picker Wheel',
      description: 'Randomly pick a color',
      icon: '🎨'
    },
    {
      path: '/tools/date-picker',
      name: 'Date Picker Wheel',
      description: 'Randomly pick a date',
      icon: '📅'
    },
    {
      path: '/tools/instagram-comment-picker',
      name: 'Instagram Comment Picker',
      description: 'Randomly pick a winner from Instagram comments',
      icon: '💬'
    },
    {
      path: '/tools/mlb-picker',
      name: 'MLB Picker Wheel',
      description: 'Randomly pick a MLB team',
      icon: '⚾'
    },
    {
      path: '/tools/nba-picker',
      name: 'NBA Picker Wheel',
      description: 'Randomly pick a NBA team',
      icon: '🏀'
    },
    {
      path: '/tools/nfl-picker',
      name: 'NFL Picker Wheel',
      description: 'Randomly pick a NFL team',
      icon: '🏈'
    },
    {
      path: '/tools/picker',
      name: 'General Picker Wheel',
      description: 'A general random picker wheel for any custom options',
      icon: '🎡'
    }
  ]

  const isAnalysisPage = location.pathname.startsWith('/decision-analysis') || location.pathname === '/analysis'
  const isWheelsPage = location.pathname.startsWith('/tools')

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">AI Decision Maker</span>
          </Link>

          <nav className="flex items-center space-x-6">
            {/* Decision Analysis Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowAnalysisDropdown(!showAnalysisDropdown)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isAnalysisPage
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span>Decision Analysis</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showAnalysisDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showAnalysisDropdown && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="p-2">
                    {analysisTypes.map((type) => (
                      <Link
                        key={type.path}
                        to={type.path}
                        onClick={() => setShowAnalysisDropdown(false)}
                        className={`flex items-center space-x-3 p-3 rounded-md transition-colors ${
                          location.pathname === type.path
                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-lg">{type.icon}</span>
                        <div className="flex-1">
                          <div className="font-medium text-sm">{type.name}</div>
                          <div className="text-xs text-gray-500">{type.description}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* More Wheels Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowWheelsDropdown(!showWheelsDropdown)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isWheelsPage
                    ? 'bg-green-100 text-green-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <span>Decision Wheels</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showWheelsDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showWheelsDropdown && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  <div className="p-2">
                    {wheelTools.map((tool) => (
                      <Link
                        key={tool.path}
                        to={tool.path}
                        onClick={() => setShowWheelsDropdown(false)}
                        className={`flex items-center space-x-3 p-3 rounded-md transition-colors ${
                          location.pathname === tool.path
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className="text-lg">{tool.icon}</span>
                        <div className="flex-1">
                          <div className="font-medium text-sm">{tool.name}</div>
                          <div className="text-xs text-gray-500">{tool.description}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* User Dropdown */}
            <UserDropdown />
          </nav>
        </div>
      </div>
      
      {/* Close dropdowns when clicking outside */}
      {(showAnalysisDropdown || showWheelsDropdown) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setShowAnalysisDropdown(false)
            setShowWheelsDropdown(false)
          }}
        />
      )}
    </header>
  )
} 